import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { concat } from '@ember/helper';
import Text from './text';
import { PhCaretUp, PhCaretDown } from 'ember-phosphor-icons';
import Card from './card';

// ---------------------------------------------------------------------------
// FaqAccordion – expandable FAQ list component
// ---------------------------------------------------------------------------

/**
 * Interface for accordion items
 */
export interface AccordionItem {
  question: string;
  answer: string;
}

/** @deprecated Use AccordionItem instead */
export type FAQItem = AccordionItem;

interface FaqAccordionSignature {
  Args: {
    /** Array of FAQ items with question and answer */
    items: AccordionItem[];
  };
  Element: HTMLDivElement;
}

/**
 * FaqAccordion - An expandable FAQ list component
 *
 * Displays a list of questions with expandable answers.
 * Only one item can be expanded at a time.
 *
 * @example Basic usage
 * ```gts
 * import FaqAccordion from 'tio-ui/components/faq-accordion';
 *
 * <FaqAccordion @items={{this.faqItems}} />
 * ```
 *
 * @example With max width
 * ```gts
 * <FaqAccordion @items={{this.faqItems}} class="max-w-3xl" />
 * ```
 */
export default class FaqAccordion extends Component<FaqAccordionSignature> {
  @tracked expandedIndex: number | null = null;

  toggleItem = (index: number) => {
    this.expandedIndex = this.expandedIndex === index ? null : index;
  };

  <template>
    <div data-test-faq-accordion class="space-y-3" ...attributes>
      {{#each @items as |faq index|}}
        {{#let
          (concat "faq-item-" index "-button") (concat "faq-item-" index "-panel")
          as |buttonId panelId|
        }}
          <Card data-test-faq-accordion-item @padding="none" class="overflow-hidden">
            <button
              data-test-faq-accordion-button
              type="button"
              id={{buttonId}}
              class="tio-row-between w-full p-4 text-left hover:tio-bg-sunken transition-colors"
              aria-expanded={{if (eq this.expandedIndex index) "true" "false"}}
              aria-controls={{panelId}}
              {{on "click" (fn this.toggleItem index)}}
            >
              <Text class="font-medium tio-fg-primary">
                {{faq.question}}
              </Text>
              {{#if (eq this.expandedIndex index)}}
                <PhCaretUp @size={{20}} class="tio-fg-disabled shrink-0" />
              {{else}}
                <PhCaretDown @size={{20}} class="tio-fg-disabled shrink-0" />
              {{/if}}
            </button>

            {{#if (eq this.expandedIndex index)}}
              <div
                data-test-faq-accordion-panel
                id={{panelId}}
                role="region"
                aria-labelledby={{buttonId}}
                class="px-4 pb-4"
              >
                <Text class="tio-fg-secondary">
                  {{faq.answer}}
                </Text>
              </div>
            {{/if}}
          </Card>
        {{/let}}
      {{/each}}
    </div>
  </template>
}

export type { FaqAccordionSignature };
